import { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  MarkEmailRead as MarkReadIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { NotificationRecipient, Notification } from '../../types';
import { mockNotificationRecipients, mockNotifications } from '../../mock';

interface NotificationCenterProps {
  userId?: string;
}

export function NotificationCenter({ userId }: NotificationCenterProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<NotificationRecipient[]>([]);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (userId) {
      const userNotifications = mockNotificationRecipients
        .filter(n => {
          const notification = mockNotifications.find(notif => notif.id === n.notificationId);
          return n.userId === userId && notification?.status === 'sent';
        })
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setNotifications(userNotifications);
    } else {
      setNotifications([]);
    }
  }, [userId]);

  const getNotificationDetails = (notificationId: string): Notification | undefined => {
    return mockNotifications.find(n => n.id === notificationId);
  };

  const unreadCount = notifications.filter(n => !n.readAt).length;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = (notificationId: string) => {
    setNotifications(notifications.map(n =>
      n.id === notificationId ? { ...n, readAt: new Date() } : n
    ));
  };

  const handleMarkAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, readAt: new Date() })));
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label={`${unreadCount} unread notifications`}
        sx={{
          color: 'text.primary',
          '&:hover': {
            bgcolor: 'grey.100',
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              fontWeight: 700,
              fontSize: '0.75rem',
            },
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<MarkReadIcon />}
              onClick={handleMarkAllAsRead}
            >
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
            {notifications.map((notification) => {
              const details = getNotificationDetails(notification.notificationId);
              return (
                <ListItem
                  key={notification.id}
                  disablePadding
                  secondaryAction={
                    !notification.readAt && (
                      <Tooltip title="Mark as read">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          sx={{
                            color: 'success.main',
                            '&:hover': {
                              bgcolor: 'success.50',
                            },
                          }}
                        >
                          <DoneIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <ListItemButton
                    onClick={() => handleMarkAsRead(notification.id)}
                    sx={{
                      bgcolor: notification.readAt ? 'transparent' : 'action.hover',
                      '&:hover': {
                        bgcolor: notification.readAt ? 'action.hover' : 'action.selected',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={notification.readAt ? 'normal' : 'bold'}>
                            {details?.title || 'Notification'}
                          </Typography>
                          {!notification.readAt && (
                            <Chip label="New" color="primary" size="small" sx={{ height: 20 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {details?.content || 'No content available'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(notification.createdAt).toLocaleString()}
                          </Typography>
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Menu>
    </>
  );
}
